// Plain-English messages for the station-configuration actions (CWA-91).
//
// Unlike the contract-draft family, these endpoints return no structured error
// body — just { timestamp, message, status, path } — and the `message` embeds
// raw state identifiers ("prospect is in state CONTRACT_SIGNED, expected …").
// Showing it would leak engineering vocabulary into the UI, so messages are
// derived from the HTTP status and the action instead.
//
// The one exception is the missing-credentials list: those are station
// identifiers, which ops uses every day and which are meaningless to paraphrase.

const status = (err: any): number | undefined => err?.response?.status;
const backendMessage = (err: any): string =>
  typeof err?.response?.data?.message === "string"
    ? err.response.data.message
    : "";

/**
 * Station identifiers from a "Missing credentials for stations: A, B" refusal.
 * Returns [] when the body does not have that shape, so callers fall back to a
 * generic message rather than rendering something misleading.
 */
export const missingCredentialStations = (err: any): string[] => {
  const match = /Missing credentials for stations:\s*(.+)$/i.exec(
    backendMessage(err)
  );
  if (!match) return [];
  return match[1]
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean);
};

export const registerStationsErrorMessage = (err: any): string => {
  const missing = missingCredentialStations(err);
  if (missing.length) {
    return `Access data is still missing for: ${missing.join(", ")}.`;
  }
  if (status(err) === 400) {
    return "The access data cannot be confirmed at the prospect's current stage.";
  }
  if (status(err) === 404) {
    return "This prospect could not be found. Reload the page and try again.";
  }
  return "The access data could not be confirmed. Please try again.";
};

export const promoteErrorMessage = (err: any): string => {
  // CWA-179: clearing can't take a second onboarding of the same customer yet (CWA-182).
  if (status(err) === 409 && err?.response?.data?.error_code === "ADDITIONAL_STATION_PROMOTION_UNSUPPORTED") {
    return "These are more stations for an existing customer, and promoting them isn't supported yet.";
  }
  if (status(err) === 400) {
    return "This prospect cannot be promoted at its current stage.";
  }
  if (status(err) === 404) {
    return "This prospect could not be found. Reload the page and try again.";
  }
  return "The prospect could not be promoted. Please try again.";
};

export const sendConfigEmailErrorMessage = (err: any): string => {
  // The backend answers 404 when no station access data exists at all — a
  // different problem from a missing prospect, and worth saying precisely.
  if (status(err) === 404) {
    return "No station access data has been recorded yet, so there is nothing to send.";
  }
  if (status(err) === 400) {
    return "The configuration email can only be sent once the prospect has been promoted to a customer.";
  }
  // CWA-232: it is already going out — on its own once eRound finished, or from a colleague's click.
  if (status(err) === 409) {
    return "The configuration email is already being sent.";
  }
  return "The configuration email could not be sent. Please try again.";
};

/**
 * Go-live and its resend share one endpoint, so they share one mapping. The
 * backend answers 409 for a wrong stage — and its message spells out the raw
 * state names ("prospect is in state CONTRACT_SIGNED, expected …"), which is
 * exactly what must never reach an agent.
 */
export const goLiveErrorMessage = (err: any): string => {
  if (status(err) === 409) {
    return "This prospect is no longer at a stage where it can go live. Reload the page and try again.";
  }
  if (status(err) === 404) {
    return "This prospect could not be found. Reload the page and try again.";
  }
  return "The station could not be marked as live. Please try again.";
};

/**
 * The first-charge welcome e-mail resend. There is deliberately no 409 case:
 * the backend performs no state check on this action at all, so the only
 * refusal it can produce is a missing prospect.
 */
export const firstChargeEmailErrorMessage = (err: any): string => {
  if (status(err) === 404) {
    return "This prospect could not be found. Reload the page and try again.";
  }
  return "The welcome email could not be sent. Please try again.";
};

export const saveCredentialsErrorMessage = (err: any): string => {
  if (status(err) === 400) {
    return "This station is not assigned to this prospect. Reload the page and try again.";
  }
  return "The access data could not be saved. Please try again.";
};

// The reveal is read-only, so the only failures worth wording are a station
// whose access data has not been recorded yet and a station that does not belong
// to this prospect.
export const revealKeyErrorMessage = (err: any): string => {
  const code = status(err);
  if (code === 404) {
    return "This station has no access data recorded yet, so there is no key to show.";
  }
  if (code === 403) {
    return "This station does not belong to this customer.";
  }
  return "The access key could not be shown. Please try again.";
};
